var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import { defineComponent, toRefs, ref, computed, watch, provide, reactive, createVNode } from "vue";
import { getPrefixCls } from "../_utils/global-config.js";
import { isFunction } from "../_utils/is.js";
import { uploadRequest, getDataURLFromFile } from "./utils.js";
import UploadButton from "./upload-button.js";
import UploadList from "./upload-list.js";
import { uploadInjectionKey } from "./context.js";
var _Upload = defineComponent({
  name: "Upload",
  components: {
    UploadButton,
    UploadList
  },
  props: {
    fileList: {
      type: Array,
      default: void 0
    },
    defaultFileList: {
      type: Array,
      default: () => []
    },
    accept: String,
    action: String,
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    directory: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    tip: String,
    headers: Object,
    data: {
      type: [Object, Function]
    },
    name: {
      type: [String, Function]
    },
    withCredentials: {
      type: Boolean,
      default: false
    },
    customRequest: {
      type: Function
    },
    limit: {
      type: Number,
      default: 0
    },
    autoUpload: {
      type: Boolean,
      default: true
    },
    showFileList: {
      type: Boolean,
      default: true
    },
    listType: {
      type: String,
      default: "text"
    },
    responseUrlKey: {
      type: String
    },
    customIcon: {
      type: Object
    },
    onBeforeUpload: {
      type: Function
    },
    onBeforeRemove: {
      type: Function
    },
    onButtonClick: {
      type: Function
    },
    onChange: {
      type: [Function, Array]
    },
    onProgress: {
      type: [Function, Array]
    },
    onExceedLimit: {
      type: [Function, Array]
    },
    onPreview: {
      type: [Function, Array]
    },
    onSuccess: {
      type: [Function, Array]
    },
    onError: {
      type: [Function, Array]
    }
  },
  emits: [
    "update:fileList",
    "exceedLimit",
    "change",
    "progress",
    "preview",
    "success",
    "error"
  ],
  setup(props, {
    emit,
    slots
  }) {
    const {
      fileList
    } = toRefs(props);
    const prefixCls = getPrefixCls("upload");
    const _fileList = ref([]);
    const fileMap = new Map();
    const requestMap = new Map();
    const isMax = computed(() => {
      return props.limit > 0 && _fileList.value.length >= props.limit;
    });
    const checkFileList = (fileList2) => {
      fileMap.clear();
      const newFileList = fileList2 == null ? void 0 : fileList2.map((data, index) => {
        var _a, _b, _c;
        const fileItem = reactive(__spreadProps(__spreadValues({}, data), {
          uid: (_a = data.uid) != null ? _a : `${Date.now()}${index}`,
          status: (_b = data.status) != null ? _b : "done",
          percent: (_c = data.percent) != null ? _c : data.status === "error" ? 0 : 1
        }));
        fileMap.set(fileItem.uid, fileItem);
        return fileItem;
      });
      _fileList.value = newFileList != null ? newFileList : [];
    };
    checkFileList(props.defaultFileList);
    watch(fileList, (fileList2) => {
      if (fileList2) {
        checkFileList(fileList2);
      }
    }, {
      immediate: true,
      deep: true
    });
    const updateFileList = (file) => {
      emit("update:fileList", _fileList.value);
      emit("change", _fileList.value, file);
    };
    const uploadFile = (fileItem) => {
      const handleProgress = (percent, event) => {
        const file = fileMap.get(fileItem.uid);
        if (file) {
          file.status = "uploading";
          file.percent = percent;
          emit("progress", file, event);
          updateFileList(file);
        }
      };
      const handleSuccess = (response) => {
        const file = fileMap.get(fileItem.uid);
        if (file) {
          file.status = "done";
          file.percent = 1;
          file.response = response;
          if (props.responseUrlKey && response[props.responseUrlKey]) {
            file.url = response[props.responseUrlKey];
          }
          requestMap.delete(file.uid);
          emit("success", file);
          updateFileList(file);
        }
      };
      const handleError = (response) => {
        const file = fileMap.get(fileItem.uid);
        if (file) {
          file.status = "error";
          file.percent = 0;
          file.response = response;
          requestMap.delete(file.uid);
          emit("error", file);
          updateFileList(file);
        }
      };
      const option = {
        fileItem,
        action: props.action,
        name: props.name,
        data: props.data,
        headers: props.headers,
        withCredentials: props.withCredentials,
        onProgress: handleProgress,
        onSuccess: handleSuccess,
        onError: handleError
      };
      fileItem.status = "uploading";
      fileItem.percent = 0;
      const request = isFunction(props.customRequest) ? props.customRequest(option) : uploadRequest(option);
      requestMap.set(fileItem.uid, request);
      updateFileList(fileItem);
    };
    const abort = (fileItem) => {
      var _a;
      const req = requestMap.get(fileItem.uid);
      if (req) {
        (_a = req.abort) == null ? void 0 : _a.call(req);
        requestMap.delete(fileItem.uid);
        const file = fileMap.get(fileItem.uid);
        if (file) {
          file.status = "error";
          file.percent = 0;
          updateFileList(file);
        }
      }
    };
    const submit = (fileItem) => {
      if (fileItem) {
        const file = fileMap.get(fileItem.uid);
        if (file) {
          uploadFile(file);
        }
      } else {
        for (const item of _fileList.value) {
          if (item.status === "init") {
            uploadFile(item);
          }
        }
      }
    };
    const initUpload = async (file, index) => {
      const uid = `${Date.now()}${index}`;
      const dataURL = await getDataURLFromFile(file);
      const fileItem = reactive({
        uid,
        file,
        url: dataURL,
        name: file.name,
        status: "init",
        percent: 0
      });
      fileMap.set(uid, fileItem);
      _fileList.value.push(fileItem);
      updateFileList(fileItem);
      if (props.autoUpload) {
        uploadFile(fileItem);
      }
    };
    const uploadFiles = (files) => {
      if (props.limit > 0 && _fileList.value.length + files.length > props.limit) {
        emit("exceedLimit", _fileList.value, files);
        return;
      }
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (isFunction(props.onBeforeUpload)) {
          Promise.resolve(props.onBeforeUpload(file)).then((result) => {
            if (result) {
              initUpload(file, i);
            }
          }).catch((err) => {
            console.error(err);
          });
        } else {
          initUpload(file, i);
        }
      }
    };
    const removeFile = (fileItem) => {
      _fileList.value.splice(_fileList.value.indexOf(fileItem), 1);
      updateFileList(fileItem);
    };
    const handleRemove = (fileItem) => {
      if (isFunction(props.onBeforeRemove)) {
        Promise.resolve(props.onBeforeRemove(fileItem)).then((result) => {
          if (result) {
            removeFile(fileItem);
          }
        }).catch((err) => {
          console.error(err);
        });
      } else {
        removeFile(fileItem);
      }
    };
    const handlePreview = (fileItem) => {
      emit("preview", fileItem);
    };
    provide(uploadInjectionKey, reactive({
      disabled: props.disabled,
      listType: props.listType,
      iconCls: `${prefixCls}-icon`,
      customIcon: props.customIcon,
      onUpload: uploadFile,
      onAbort: abort,
      onRemove: handleRemove,
      onPreview: handlePreview
    }));
    const renderButton = () => {
      return createVNode(UploadButton, {
        "key": "arco-upload-button",
        "disabled": props.disabled,
        "draggable": props.draggable,
        "listType": props.listType,
        "isMax": isMax.value,
        "uploadFiles": uploadFiles,
        "multiple": props.multiple,
        "directory": props.directory,
        "tip": props.tip,
        "accept": props.accept
      }, {
        default: slots["upload-button"]
      });
    };
    const render = () => {
      if (!props.showFileList) {
        return renderButton();
      }
      return createVNode("div", {
        "class": [`${prefixCls}-wrapper`, `${prefixCls}-wrapper-type-${props.listType}`]
      }, [props.listType !== "picture-card" && renderButton(), createVNode(UploadList, {
        "fileList": _fileList.value,
        "listType": props.listType
      }, {
        "upload-button": renderButton,
        "upload-item": slots["upload-item"]
      })]);
    };
    return {
      prefixCls,
      submit,
      abort,
      render
    };
  },
  render() {
    return this.render();
  }
});
export { _Upload as default };
